// Author: Çağrı Erdem, 2024
// Description: main script for the new dB study


////////////////////////////////////////////////////////////////////////////////////////    
// NOTES:
// Typical Frequency Bands Filtered in Eye-Tracking Interfaces:
// In eye-tracking systems, noise usually comes from external factors like head movement, environmental vibrations, or sensor imperfections. The signal of interest typically lies within certain frequency bands,
// such as
// Fixations: When the eyes are relatively still (e.g., focusing on a target), the signal is generally below 5 Hz.
// Saccades: Rapid eye movements between fixations, which typically occur at frequencies between 2 Hz and 10 Hz.
// Tremors/Noise: High-frequency noise from sensor vibrations or other sources may occur above 10 Hz.
// Thus, most filtering for eye-tracking is done using low-pass filters with a cutoff frequency typically set between 5 Hz and 10 Hz, depending on the specific application and level of noise in the system.
////////////////////////////////////////////////////////////////////////////////////////    

//TODO: Use an OffscreenCanvas worker to handle all rendering tasks.


import { participantID, sessionID } from './interface.js';
import { captureAndProcessData, saveDataToFile, toggleRecording } from './recBatch.js';
import { DrawingUtils, FaceLandmarker, FilesetResolver } from "./tasks-vision.js";
import { findScoreForCategory } from './utils/utils.js';

const video = document.getElementById("webcam");
const canvasElement = document.getElementById("output_canvas");
const canvasCtx = canvasElement.getContext("2d");
const cameraSelect = document.getElementById("cameraSelect"); // Dropdown for camera selection
const startTrackingCheckbox = document.getElementById("startTracking"); // Checkbox for start tracking
const enableDrawingCheckbox = document.getElementById("showTracking");
const showWebcamCheckbox = document.getElementById("showWebcam"); // Checkbox for show/hide webcam
const zoomSlider = document.getElementById("zoomSlider");
const calibrationButton = document.getElementById('calibrationButton'); // Calibration button to reset the val


let faceLandmarker;
let pose;
let runningMode = "IMAGE";
let webcamRunning = false;
let currentStream = null; // To store the current camera stream
const videoWidth = 480;
let zoomFactor = 1.0;

// Calibration variables
let latestPoseResults = null;

// Unified calibration data
let calibrationData = {
    poseLandmarks: null,
    gazeCenter: null,
    roll: 0,
    faceLandmarks: null,
    faceBlendshapes: null // Store calibrated blendshapes
};


// Fixed position for the static plus symbol (in the center of the canvas)
const fixedPlusPosition = { x: canvasElement.width / 0.9, y: canvasElement.height / 1.25};
// Define the dynamic plus symbol position
let dynamicPlusPosition = { x: 0, y: 0 };


///////////////////////////////////////////////
///////////////////////////////////////////////
// Worker Initialization
const facePoseWorker = new Worker(new URL('./workers/facePoseWorker.js', import.meta.url), { type: 'module' });
let isWorkerReady = false; // Tracks worker readiness


// Initialize FaceLandmarker and Pose Detection in the main thread
async function preLoadAssets() {
    const filesetResolver = await FilesetResolver.forVisionTasks("https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.0/wasm");

    faceLandmarker = await FaceLandmarker.createFromOptions(filesetResolver, {
        baseOptions: {
            modelAssetPath: `https://storage.googleapis.com/mediapipe-models/face_landmarker/face_landmarker/float16/1/face_landmarker.task`,
            delegate: "GPU"
        },
        outputFaceBlendshapes: true,
        runningMode,
        numFaces: 1
    });

    // Initialize Pose using the traditional MediaPipe API
    pose = new Pose({
        locateFile: (file) => `https://cdn.jsdelivr.net/npm/@mediapipe/pose/${file}`
    });

    pose.setOptions({
        modelComplexity: 1, // 0 for less complex, 1 is default, 2 is most complex
        smoothLandmarks: true,
        minDetectionConfidence: 0.5,
        minTrackingConfidence: 0.5
    });

    // Set up pose.onResults to handle results and update latestPoseResults
    pose.onResults(onPoseResults);

    // Mark assets as ready
    isWorkerReady = true; // Set this flag to indicate assets are ready
    console.log("Assets preloaded and ready.");
}
preLoadAssets();
///////////////////////////////////////////////



///////////////////////////////////////////////
// Camera and tracking functions
///////////////////////////////////////////////

// Check if webcam access is supported
function hasGetUserMedia() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
}


// Function to list all available cameras
async function getCameraDevices() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(device => device.kind === 'videoinput');
  
      if (videoDevices.length === 0) {
        alert('No camera devices found.');
        return;
      }
  
      cameraSelect.innerHTML = '';
  
      videoDevices.forEach((device, index) => {
        const option = document.createElement('option');
        option.value = device.deviceId;
        option.text = device.label || `Camera ${index + 1}`;
        cameraSelect.appendChild(option);
      });
    } catch (error) {
      console.error('Error fetching camera devices:', error);
      alert('Unable to access camera devices. Please check your browser settings.');
    }
  }


// Enable or disable the webcam view and start detection based on checkbox
function toggleTracking(event) {
    if (!faceLandmarker) {
        console.log("Wait! faceLandmarker not loaded yet.");
        return;
    }

    if (event.target.checked) {
        webcamRunning = true;
        startCamera(cameraSelect.value);
        video.style.display = "none";  // Ensure the webcam is hidden by default
        showWebcamCheckbox.checked = false; // Reset the webcam visibility checkbox
    } else {
        webcamRunning = false;
        stopCamera(); // Stop the camera stream and clear video
    }
}


// Start camera with selected device
async function startCamera(deviceId) {
    const constraints = {
        video: {
            deviceId: deviceId ? { exact: deviceId } : undefined,
            width: { ideal: 640 }, // Lower resolution, e.g., 640x480
            height: { ideal: 480 }
        }
    };

    try {
        currentStream = await navigator.mediaDevices.getUserMedia(constraints);
        video.srcObject = currentStream;
        video.addEventListener("loadeddata", predictWebcam);
    } catch (error) {
        console.error('Error accessing webcam:', error);
        alert('Camera access denied or unavailable. Please check your permissions and try again.');
    }
}

// Stop the camera and clear the video element
function stopCamera() {
    if (currentStream) {
        currentStream.getTracks().forEach(track => track.stop());
        currentStream = null;
    }
    video.srcObject = null;
    canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height); // Clear the canvas
}
///////////////////////////////////////////////
///////////////////////////////////////////////



////////////////////////////////
////////////////////////////////
////////////////////////////////
// Prediction and drawing loop//
////////////////////////////////
////////////////////////////////
////////////////////////////////

let lastVideoTime = -1;
let results = undefined;
const drawingUtils = new DrawingUtils(canvasCtx);

// frame rate related
let lastProcessedTime = 0;
const frameInterval = 100; // e.g., process every 100ms (10 fps)
let frameCounter = 0;

// // Frame Skipping to reduce load
// frameCounter++;
// if (frameCounter % 3 !== 0) {  // Process every 3rd frame
//     requestAnimationFrame(predictWebcam);
//     return;
// }

//Without throttling, predictWebcam runs on every single frame, which is very demanding. 
//If the webcam capture, landmark detection, worker processing, and drawing are all happening without any regulation, 
//it can overwhelm the CPU/GPU, leading to missed frames or timing issues where the worker doesn't receive/process data quickly enough.

async function predictWebcam() {
    if (!webcamRunning) return;

    // Frame throttling to reduce load
    const now = Date.now();
    if (now - lastProcessedTime < frameInterval) {
        requestAnimationFrame(predictWebcam);
        return;
    }
    lastProcessedTime = now;

    // Resize the video and canvas elements based on the video feed's aspect ratio and zoom factor
    const ratio = video.videoHeight / video.videoWidth;
    video.style.width = (videoWidth * zoomFactor) + "px";
    video.style.height = (videoWidth * ratio * zoomFactor) + "px";
    canvasElement.style.width = (videoWidth * zoomFactor) + "px";
    canvasElement.style.height = (videoWidth * ratio * zoomFactor) + "px";
    canvasElement.width = video.videoWidth;
    canvasElement.height = video.videoHeight;

    if (runningMode === "IMAGE") {
        runningMode = "VIDEO";
        await faceLandmarker.setOptions({
            runningMode: runningMode,
            outputFaceBlendshapes: true
        });
    }

    let nowInMs = Date.now();
    if (lastVideoTime !== video.currentTime) {
        lastVideoTime = video.currentTime;
        results = faceLandmarker.detectForVideo(video, nowInMs); // Detect face landmarks on the current video frame
        await pose.send({ image: video }); // Send the video frame for pose landmarks detection
    }
    // Clear canvas for fresh drawing
    canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);

    // Send landmarks to worker for smoothing and further processing
    if (results.faceLandmarks || latestPoseResults?.poseLandmarks || results.faceBlendshapes) {
        facePoseWorker.postMessage({
            type: 'PROCESS_FRAME',
            results: {
                faceLandmarks: results.faceLandmarks?.[0] || [],
                poseLandmarks: latestPoseResults?.poseLandmarks || [],
                blendShapes: results.faceBlendshapes[0] || []
            }
        });
    }

    // Continue running prediction if the webcam is still running
    if (webcamRunning) {
        window.requestAnimationFrame(predictWebcam);
    }
}


let updateQueue = []; // Queue for managing drawing updates
let headOrientation = {pitch: 0, roll: 0, yaw: 0};

// Handle worker messages to draw refined results on the canvas
facePoseWorker.onmessage = (event) => {
    if (event.data.type === 'PROCESSED_RESULTS') {
        const { faceLandmarks, poseLandmarks, blendShapes } = event.data.data;

        // Handle missing or empty data gracefully
        if ((!faceLandmarks || faceLandmarks.length === 0) && (!poseLandmarks || poseLandmarks.length === 0)) {
            console.warn("No valid face or pose landmarks received, skipping draw.");
            return;
        }

        // Add the received data to the update queue
        updateQueue.push({ faceLandmarks, poseLandmarks, blendShapes });

        for (const landmarks of results.faceLandmarks) {
            // just for calibration purposes
            // const roll = calculateRoll(landmarks);
             // updateRollSlider(roll);
             headOrientation = calculateHeadOrientation(landmarks);
            updateHeadMotionSliders(headOrientation.pitch, headOrientation.roll, headOrientation.yaw);
        }


    //     if (enableDrawingCheckbox.checked) {

    //         drawPlus(fixedPlusPosition.x, fixedPlusPosition.y, 'pink'); //TODO: adjust it according to the expected sitting positinon
    //         drawFace(faceLandmarks);
    //         updateRollSlider(roll);
    //         updateFaceLandmarks(faceLandmarks);

    //         // Calculate the position of the dynamic plus symbol based on nose tip (landmark 1) for calibration
    //         const noseTip = faceLandmarks[1]; 
    //         if (noseTip) {
    //             dynamicPlusPosition.x = noseTip.x * canvasElement.width;
    //             dynamicPlusPosition.y = noseTip.y * canvasElement.height;
    //             drawPlus(dynamicPlusPosition.x, dynamicPlusPosition.y, 'red'); 
    //         }
    //     }
    
    //     // Draw pose landmarks if available
    //     if (!poseLandmarks || poseLandmarks.length === 0) {
    //         console.warn("No valid pose landmarks received, skipping draw.");
    //     } else if (enableDrawingCheckbox.checked) {
    //         drawPoseLandmarks(poseLandmarks);
    //     }

    //     if (blendShapes && Object.keys(blendShapes).length > 0) {
    //         calibrateEyeGaze(blendShapes);
    //         // updateBlendShapeDisplay(blendShapes);
    //     } else {
    //         console.warn("No valid blend shapes received, skipping update.");
    //     }


    //     // Check for poor lighting conditions in real-time
    //     if (detectRealTimePoorLighting(faceLandmarks, jitterThreshold = 0.005)) { //0.001 dene
    //         showLightingWarning();
    //         console.warn("Poor lighting detected. Adjust lighting conditions for better tracking.");
    //     }

    //     // Record face landmarks and other data (pose landmarks and blendshapes)
    //     captureAndProcessData(
    //         faceLandmarks || [],
    //         poseLandmarks || [],
    //         blendShapes || [], 
    //         roll
    //     );

    //     if (verbose) {
    //         console.log('Face: ', faceLandmarks);
    //         console.log('Pose: ', poseLandmarks);
    //         console.log('Blendshapes: ', blendShapes);
    //     }

    //     // Check alignment and roll conditions for calibration
    //     const rollValue = parseFloat(document.getElementById('rollSlider').value);
    //     const shouldersAligned = areShouldersAligned(poseLandmarks);

    //     if (isAligned() && rollValue >= -0.2 && rollValue <= 0.2 && shouldersAligned) {
    //         displayCalibrationPrompt();
    //         console.log("Hit the Calibrate button");
    //         // TODO: Adapt the calibration approach to the new 'refined' landmarks
    //     }
    }
};


// Drawing function to handle rendering from the queue
function drawFromQueue() {
    if (updateQueue.length > 0) {
        let verbose = false
        // Use the latest data from the queue
        const { faceLandmarks, poseLandmarks, blendShapes } = updateQueue.shift();

        if (verbose) {
            console.log('Face: ', faceLandmarks);
            console.log('Pose: ', poseLandmarks);
            console.log('Blendshapes: ', blendShapes);
        }

        // Clear canvas for fresh drawing
        canvasCtx.clearRect(0, 0, canvasElement.width, canvasElement.height);

        if (enableDrawingCheckbox.checked) {
            // Draw fixed reference point
            drawPlus(fixedPlusPosition.x, fixedPlusPosition.y, 'pink'); // TODO: adjust according to expected sitting position

            // Draw face landmarks if available
            if (faceLandmarks && faceLandmarks.length > 0) {
                updateFaceLandmarks(faceLandmarks);
                drawFace(faceLandmarks);

                // const noseTip = faceLandmarks[1]; //simdi artik part ismine gore buluyoruz
                const noseTip = faceLandmarks.find(landmark => landmark.part === 'nose_1');
                if (noseTip) {
                    dynamicPlusPosition.x = noseTip.x * canvasElement.width;
                    dynamicPlusPosition.y = noseTip.y * canvasElement.height;
                    drawPlus(dynamicPlusPosition.x, dynamicPlusPosition.y, 'red');
                }
            }

            // Draw pose landmarks if available
            if (poseLandmarks && poseLandmarks.length > 0) {
                drawPoseLandmarks(poseLandmarks);
                // drawPoseConnections(poseLandmarks);
            }

            // Handle blend shapes if available
            if (blendShapes && Object.keys(blendShapes).length > 0) {
                calibrateEyeGaze(blendShapes);
                // updateBlendShapeDisplay(blendShapes);
            }
        }

        // Check for poor lighting conditions in real-time
        if (faceLandmarks && detectRealTimePoorLighting(faceLandmarks)) {
            showLightingWarning();
            console.warn("Poor lighting detected. Adjust lighting conditions for better tracking.");
        }

        // Record face landmarks and other data (pose landmarks and blendshapes)
        if (faceLandmarks || poseLandmarks || blendShapes) {
            // console.log("Recording data: ", faceLandmarks, poseLandmarks, blendShapes);
            captureAndProcessData(
                faceLandmarks || [],
                poseLandmarks || [],
                blendShapes || [],
                headOrientation || [],
            );
        }

        // Check alignment and roll conditions for calibration
        const rollValue = parseFloat(document.getElementById('rollSlider').value);
        const shouldersAligned = areShouldersAligned(poseLandmarks);
        if (isAligned() && rollValue >= -0.2 && rollValue <= 0.2 && shouldersAligned) {
            if (enableDrawingCheckbox.checked && !isCalibrated) {
                displayCalibrationPrompt();
                console.log("Hit the Calibrate button");
            }
        }
    }

    // Request the next frame for drawing
    window.requestAnimationFrame(drawFromQueue);
}

// Start the drawing loop
window.requestAnimationFrame(drawFromQueue);





///////////////////////////////////////////////
///////////////////////////////////////////////
zoomSlider.addEventListener("input", (event) => {
    zoomFactor = event.target.value;
  });
///////////////////////////////////////////////
///////////////////////////////////////////////


/////////////////////
// DRAWing helpers //
/////////////////////

// Pose display elements
const noseTipDisplay = document.getElementById('noseTip');
const leftShoulderDisplay = document.getElementById('leftShoulder');
const rightShoulderDisplay = document.getElementById('rightShoulder');
const leftElbowDisplay = document.getElementById('leftElbow');
const rightElbowDisplay = document.getElementById('rightElbow');
const leftHandDisplay = document.getElementById('leftHand');
const rightHandDisplay = document.getElementById('rightHand');


// Function to draw selected face landmarks on the canvas
function drawFace(selectedLandmarks) {
    if (!selectedLandmarks || selectedLandmarks.length === 0) {
        return;
    }

    // Draw facial landmarks as points (hot pink with black stroke)
    canvasCtx.fillStyle = 'rgba(255, 105, 180, 0.9)'; // Hot pink with high opacity
    canvasCtx.lineWidth = 1.5;
    canvasCtx.strokeStyle = 'rgba(0, 0, 0, 0.6)'; // Black stroke for better contrast

    selectedLandmarks.forEach(point => {
        if (point && !isNaN(point.x) && !isNaN(point.y)) {
            const x = point.x * canvasElement.width;
            const y = point.y * canvasElement.height;

            // Draw a filled circle with a stroke
            canvasCtx.beginPath();
            canvasCtx.arc(x, y, 3, 0, 2 * Math.PI);
            canvasCtx.fill();
            canvasCtx.stroke();
        }
    });

    // Draw face oval connections using quadratic curves
    const faceOvalLabels = [
        'face_oval_1', 'face_oval_2', 'face_oval_3', 'face_oval_4',
        'face_oval_5', 'face_oval_6', 'face_oval_7', 'face_oval_8',
        'face_oval_9', 'face_oval_10', 'face_oval_11'
    ];
    const faceOvalPoints = faceOvalLabels.map(label => selectedLandmarks.find(landmark => landmark.part === label));
    drawFeatureConnections(faceOvalPoints, 'rgba(255, 20, 147, 0.6)'); // Deep pink for the connections

    // Draw left eye connections using quadratic curves
    const leftEyeLabels = ['left_iris_1', 'left_iris_2', 'left_iris_3', 'left_iris_4'];
    const leftEyePoints = leftEyeLabels.map(label => selectedLandmarks.find(landmark => landmark.part === label));
    drawFeatureConnections(leftEyePoints, 'rgba(0, 255, 0, 0.8)'); // Green for the left eye

    // Draw right eye connections using quadratic curves
    const rightEyeLabels = ['right_iris_1', 'right_iris_2', 'right_iris_3', 'right_iris_4'];
    const rightEyePoints = rightEyeLabels.map(label => selectedLandmarks.find(landmark => landmark.part === label));
    drawFeatureConnections(rightEyePoints, 'rgba(0, 0, 255, 0.8)'); // Blue for the right eye

    //TODO: Add more later
    // // Draw nose connections using quadratic curves
    // const noseLabels = ['nose_1', 'nose_2', 'nose_3', 'nose_4', 'nose_5', 'nose_6'];
    // const nosePoints = noseLabels.map(label => selectedLandmarks.find(landmark => landmark.part === label));
    // drawFeatureConnections(nosePoints, 'rgba(255, 255, 0, 0.8)'); // Yellow for the nose

    // // Draw mouth connections using quadratic curves
    // const mouthLabels = ['lip_bottom_1', 'lip_bottom_2', 'lip_bottom_3', 'lip_bottom_4', 'lip_bottom_5', 'lip_bottom_6', 'lip_bottom_7', 'lip_bottom_8', 'lip_bottom_9'];
    // const mouthPoints = mouthLabels.map(label => selectedLandmarks.find(landmark => landmark.part === label));
    // drawFeatureConnections(mouthPoints, 'rgba(255, 69, 0, 0.8)'); // Orange for the mouth
}


function drawFeatureConnections(landmarks, color) {
    if (!landmarks || landmarks.length < 2) {
        return; // Need at least two points to draw connections
    }

    // Set line style for drawing the feature connections
    canvasCtx.strokeStyle = color; // Color for the feature connections
    canvasCtx.lineWidth = 2;

    // Begin drawing the feature connections
    canvasCtx.beginPath();

    // Start from the first point
    const firstPoint = landmarks[0];
    if (firstPoint && !isNaN(firstPoint.x) && !isNaN(firstPoint.y)) {
        canvasCtx.moveTo(firstPoint.x * canvasElement.width, firstPoint.y * canvasElement.height);
    }

    // Draw quadratic curves between each point for smoother transitions
    for (let i = 0; i < landmarks.length - 1; i++) {
        const currentPoint = landmarks[i];
        const nextPoint = landmarks[i + 1];

        if (currentPoint && nextPoint && !isNaN(currentPoint.x) && !isNaN(currentPoint.y) && !isNaN(nextPoint.x) && !isNaN(nextPoint.y)) {
            // Calculate a control point for the curve - midpoint between current and next points
            const controlPointX = (currentPoint.x + nextPoint.x) / 2;
            const controlPointY = (currentPoint.y + nextPoint.y) / 2;

            canvasCtx.quadraticCurveTo(
                currentPoint.x * canvasElement.width, currentPoint.y * canvasElement.height,
                controlPointX * canvasElement.width, controlPointY * canvasElement.height
            );
        }
    }

    // Close the path to complete the feature shape with a smooth curve
    const lastPoint = landmarks[landmarks.length - 1];
    if (lastPoint && !isNaN(lastPoint.x) && !isNaN(lastPoint.y) && firstPoint && !isNaN(firstPoint.x) && !isNaN(firstPoint.y)) {
        // Calculate a control point between the last and first points
        const controlPointX = (lastPoint.x + firstPoint.x) / 2;
        const controlPointY = (lastPoint.y + firstPoint.y) / 2;

        // Draw a curve from the last point to the first point to close the loop
        canvasCtx.quadraticCurveTo(
            lastPoint.x * canvasElement.width, lastPoint.y * canvasElement.height,
            controlPointX * canvasElement.width, controlPointY * canvasElement.height
        );

        // Move to the first point to properly close the path
        canvasCtx.lineTo(firstPoint.x * canvasElement.width, firstPoint.y * canvasElement.height);
    }

    // Draw the completed feature connections
    canvasCtx.stroke();
}




// Draw pose landmarks on the canvas
let filterPoseLandmarks = false;
// Draw pose landmarks on the canvas
function drawPoseLandmarks(poseLandmarks, toFilter = false) {
    if (!poseLandmarks || poseLandmarks.length === 0) {
        return;
    }
    let procLandmarks = [];

    if (toFilter) {
        // Filter landmarks to exclude 0-10 and 17-22
        for (let index = 0; index < poseLandmarks.length; index++) {
            if (index >= 11 && index <= 24 && ![17, 18, 19, 20, 21, 22].includes(index)) {
                procLandmarks.push(poseLandmarks[index]);
            }
        }

        // Draw each visible landmark as a small circle
        procLandmarks.forEach((landmark) => {
            canvasCtx.fillStyle = 'rgba(0, 255, 127, 0.8)'; // Spring green with slight transparency
            canvasCtx.lineWidth = 1.5;
            canvasCtx.strokeStyle = 'rgba(0, 0, 0, 0.6)'; // Black stroke for contrast
            canvasCtx.beginPath();
            canvasCtx.arc(landmark.x * canvasElement.width, landmark.y * canvasElement.height, 4, 0, 2 * Math.PI);
            canvasCtx.fill();
            canvasCtx.stroke();
        });

        // Draw pose connections for filtered landmarks
        drawPoseConnections(procLandmarks, toFilter, true);
    } else {
        // Draw specified landmarks: 11, 12, 13, 14, 15, 16 (given in order starting at index 0)
        procLandmarks = poseLandmarks;

        // Draw each visible landmark as a small circle
        procLandmarks.forEach((landmark) => {
            canvasCtx.fillStyle = 'rgba(0, 255, 127, 0.8)'; // Spring green with slight transparency
            canvasCtx.lineWidth = 1.5;
            canvasCtx.strokeStyle = 'rgba(0, 0, 0, 0.6)'; // Black stroke for contrast
            canvasCtx.beginPath();
            canvasCtx.arc(landmark.x * canvasElement.width, landmark.y * canvasElement.height, 4, 0, 2 * Math.PI);
            canvasCtx.fill();
            canvasCtx.stroke();
        });

        // Draw pose connections for specified landmarks
        drawPoseConnections(procLandmarks, toFilter, false);
    }
}

// Update the drawPoseConnections function to only draw connections for the visible landmarks
function drawPoseConnections(landmarks, toFilter = false, neck = false) {
    if (!landmarks || landmarks.length === 0) {
        return;
    }
    let connections = [];

    if (toFilter) {
        // Connections specific to the filtered landmarks (11 to 24 excluding 17-22)
        connections = [
            [11, 12], // Shoulders
            [11, 23], // Left shoulder to left hip
            [12, 24], // Right shoulder to right hip
            [23, 24], // Hips
            [11, 13], [13, 15], // Left arm
            [12, 14], [14, 16], // Right arm
            [23, 25], [25, 27], // Left leg
            [24, 26], [26, 28]  // Right leg
        ];
    } else {
        // Connections for the specified landmarks: 11, 12, 13, 14, 15, 16
        connections = [
            [0, 1], // Shoulders
            [0, 2], [2, 4], // Left arm
            [1, 3], [3, 5]  // Right arm
        ];
    }

    canvasCtx.lineWidth = 2.5;
    canvasCtx.strokeStyle = 'rgba(30, 144, 255, 0.7)'; // Dodger blue with slight transparency

    // Draw each connection
    connections.forEach(([start, end]) => {
        if (landmarks[start] && landmarks[end]) {
            canvasCtx.beginPath();
            canvasCtx.moveTo(landmarks[start].x * canvasElement.width, landmarks[start].y * canvasElement.height);
            canvasCtx.lineTo(landmarks[end].x * canvasElement.width, landmarks[end].y * canvasElement.height);
            canvasCtx.stroke();
        }
    });

    if (neck) {
        // Draw neck connection if needed
        const neckPoint = {
            x: (landmarks[0].x + landmarks[1].x) / 2,
            y: (landmarks[0].y + landmarks[1].y) / 2 - 0.11 // Decreased offset moves neck up
        };

        const spineBase = {
            x: (landmarks[0].x + landmarks[1].x) / 2,
            y: (landmarks[0].y + landmarks[1].y) / 2
        };

        canvasCtx.strokeStyle = '#FF00FF'; // Color for the neck/spine line
        canvasCtx.beginPath();
        canvasCtx.moveTo(neckPoint.x * canvasElement.width, neckPoint.y * canvasElement.height);
        canvasCtx.lineTo(spineBase.x * canvasElement.width, spineBase.y * canvasElement.height);
        canvasCtx.stroke();
    }
}




// Function to calculate distance between two points
function calculateDistance(point1, point2) {
    const dx = point1.x - point2.x;
    const dy = point1.y - point2.y;
    return Math.sqrt(dx * dx + dy * dy);
}

// Function to handle pose results
function onPoseResults(results) {
    latestPoseResults = results;
}


// Function to draw the point on the canvas
function drawPoint(x, y) {
    // Ensure the canvas and context are valid
    if (!ctx) {
        console.error("Canvas context not initialized");
        return;
    }

    // Debug: Check the mapped coordinates
    console.log(`Drawing point at X: ${x}, Y: ${y}`);

    // Clear the canvas for the new frame
    ctx.clearRect(0, 0, canvasWidth, canvasHeight);

    // Draw the point on the canvas
    ctx.beginPath();
    ctx.arc(x, y, 10, 0, 2 * Math.PI);  // Draw a circle with radius 10 at (x, y)
    ctx.fillStyle = "red";               // Set fill color
    ctx.fill();                          // Fill the circle
}


// Function to draw a plus symbol at a given position
function drawPlus(x, y, color) {
    const size = 10; // Size of the plus symbol
    canvasCtx.strokeStyle = color;
    canvasCtx.lineWidth = 2;

    // Draw horizontal line
    canvasCtx.beginPath();
    canvasCtx.moveTo(x - size, y);
    canvasCtx.lineTo(x + size, y);
    canvasCtx.stroke();

    // Draw vertical line
    canvasCtx.beginPath();
    canvasCtx.moveTo(x, y - size);
    canvasCtx.lineTo(x, y + size);
    canvasCtx.stroke();
}






////////////////////////////
//////////////////////////// Draggable canvas
////////////////////////////


document.addEventListener("DOMContentLoaded", () => {
    const draggableContainer = document.getElementById('draggableContainer');
    const dragHandle = document.getElementById('dragHandle');

    let isDragging = false;
    let offsetX, offsetY;

    // Start dragging when the handle is clicked
    dragHandle.addEventListener('mousedown', (e) => {
        isDragging = true;
        offsetX = e.clientX - draggableContainer.getBoundingClientRect().left;
        offsetY = e.clientY - draggableContainer.getBoundingClientRect().top;
        dragHandle.style.cursor = 'grabbing';
    });

    // Drag the container on mouse move
    document.addEventListener('mousemove', (e) => {
        if (!isDragging) return;

        const x = e.clientX - offsetX;
        const y = e.clientY - offsetY;

        // Update the container's position
        draggableContainer.style.left = `${x}px`;
        draggableContainer.style.top = `${y}px`;
    });

    // Stop dragging on mouse up
    document.addEventListener('mouseup', () => {
        if (isDragging) {
            isDragging = false;
            dragHandle.style.cursor = 'grab';
        }
    });
});








////////////////////
///Calibration and data recording Helpers/////
//////////////////

// Function to update face landmarks relative to the calibrated position
function updateFaceLandmarks(faceLandmarks, verbose = false) {
    if (!calibrationData.faceLandmarks) return;

    // Calculate differences from calibrated landmarks
    const differences = faceLandmarks.map((landmark, index) => ({
        x: landmark.x - calibrationData.faceLandmarks[index].x,
        y: landmark.y - calibrationData.faceLandmarks[index].y,
        z: landmark.z - calibrationData.faceLandmarks[index].z
    }));

    if (verbose) {
        console.log('Face landmarks differences:', differences);
        // console.log('Calibrated face landmarks:', calibrationData.faceLandmarks);
        // console.log('Current face landmarks:', faceLandmarks);
    }
    
}


// Function to update pose metrics relative to the calibrated position
function updatePoseMetrics(results) {
    const landmarks = results.poseLandmarks;

    // Calculate distances or angles for each landmark group relative to the calibrated position
    const calibratedLandmarks = calibrationData.poseLandmarks;
    const leftShoulder = calculateDistance(landmarks[11], calibratedLandmarks ? calibratedLandmarks[11] : landmarks[11]);
    const rightShoulder = calculateDistance(landmarks[12], calibratedLandmarks ? calibratedLandmarks[12] : landmarks[12]);
    const leftElbow = calculateDistance(landmarks[13], calibratedLandmarks ? calibratedLandmarks[13] : landmarks[13]);
    const rightElbow = calculateDistance(landmarks[14], calibratedLandmarks ? calibratedLandmarks[14] : landmarks[14]);
    const leftHand = calculateDistance(landmarks[15], calibratedLandmarks ? calibratedLandmarks[15] : landmarks[15]);
    const rightHand = calculateDistance(landmarks[16], calibratedLandmarks ? calibratedLandmarks[16] : landmarks[16]);

    // Update display elements
    if (leftShoulderDisplay) leftShoulderDisplay.textContent = `Left Shoulder: ${leftShoulder.toFixed(4)}`;
    if (rightShoulderDisplay) rightShoulderDisplay.textContent = `Right Shoulder: ${rightShoulder.toFixed(4)}`;
    if (leftElbowDisplay) leftElbowDisplay.textContent = `Left Elbow: ${leftElbow.toFixed(4)}`;
    if (rightElbowDisplay) rightElbowDisplay.textContent = `Right Elbow: ${rightElbow.toFixed(4)}`;
    if (leftHandDisplay) leftHandDisplay.textContent = `Left Hand: ${leftHand.toFixed(4)}`;
    if (rightHandDisplay) rightHandDisplay.textContent = `Right Hand: ${rightHand.toFixed(4)}`;
}



function showLightingWarning() {
    const warningOverlay = document.getElementById("lightingWarning");
    warningOverlay.classList.add("show");
    // Auto-hide after 5 seconds
    setTimeout(() => {
        warningOverlay.classList.remove("show");
    }, 5000);
}


// Function to check if the plus symbols are aligned
function isAligned() {
    const alignmentThreshold = 15; // Pixel threshold to consider symbols as aligned
    const deltaX = Math.abs(fixedPlusPosition.x - dynamicPlusPosition.x);
    const deltaY = Math.abs(fixedPlusPosition.y - dynamicPlusPosition.y);
    return deltaX <= alignmentThreshold && deltaY <= alignmentThreshold;
}

// Function to check if the shoulders are aligned
function areShouldersAligned(poseLandmarks, isReduced = true) {
    if (poseLandmarks && poseLandmarks.length > 0) {
        const leftShoulder = poseLandmarks[11] ? poseLandmarks[11] : (isReduced = true, poseLandmarks[0]);
        const rightShoulder = poseLandmarks[12] ? poseLandmarks[12] : (isReduced = true, poseLandmarks[1]);
        // console.log('Left shoulder:', leftShoulder);

        const yDifference = Math.abs(leftShoulder.y - rightShoulder.y);
        // console.log('Shoulder Y-axis difference:', yDifference);
        const alignmentThreshold = 0.05; // Y-axis threshold for shoulder alignment

        // Check if the shoulders are within the acceptable Y-axis range
        return yDifference <= alignmentThreshold;
    }

    return false; // Shoulders are not aligned if landmarks are unavailable
}

// Function to display a prompt on the canvas
function displayCalibrationPrompt() {
    // TODO: Work on the style and positioning of the text
    canvasCtx.font = "20px Arial";
    canvasCtx.fillStyle = "green";
    canvasCtx.textAlign = "center";
    canvasCtx.fillText("Hit the Calibrate button", canvasElement.width / 2, canvasElement.height / 2 + 50);
}



// Function to update eye gaze relative to the calibrated gaze center
function calibrateEyeGaze(blendShapes, verbose = false) {
    if (!calibrationData.gazeCenter) return;

    const leftLookOutDiff = findScoreForCategory(blendShapes, 'eyeLookOutLeft') - calibrationData.gazeCenter.leftLookOut;
    const rightLookOutDiff = findScoreForCategory(blendShapes, 'eyeLookOutRight') - calibrationData.gazeCenter.rightLookOut;
    const leftLookInDiff = findScoreForCategory(blendShapes, 'eyeLookInLeft') - calibrationData.gazeCenter.leftLookIn;
    const rightLookInDiff = findScoreForCategory(blendShapes, 'eyeLookInRight') - calibrationData.gazeCenter.rightLookIn;

    if (verbose) {
        console.log('Eye gaze differences:', {
            leftLookOutDiff,
            rightLookOutDiff,
            leftLookInDiff,
            rightLookInDiff
        });
    }
}


// // Function to calculate the roll based on the left and right eye positions
// function calculateRoll(faceLandmarks) {
//     const leftEye = faceLandmarks[33];  // Left eye outer corner
//     const rightEye = faceLandmarks[263];  // Right eye outer corner
//     // console.log('Left eye:', leftEye, 'Right eye:', rightEye);

//     // Calculate the roll as the difference in the vertical positions of the eyes
//     const roll = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * (180 / Math.PI);
//     let rollInvert = 1 - roll; // Invert the roll for the slider
//     let mappedRoll = mapRange(rollInvert, -0.4, 0.4, -1, 1); //TODO: check the range and whether it's needed

//     return mappedRoll;
// }

// // Function to normalize roll and update the slider
// function updateRollSlider(roll) {
//     const rollSlider = document.getElementById('rollSlider');

//     // Assuming roll ranges from -90 to 90 degrees (left tilt to right tilt)
//     const normalizedRoll = normalizeAngle(roll, -90, 90);

//     // Set the slider value to the normalized roll
//     rollSlider.value = normalizedRoll;
// }

// Helper function to normalize angles to the slider range (-1 to 1)
function normalizeAngle(angle, minAngle, maxAngle) {
    return ((angle - minAngle) / (maxAngle - minAngle)) * 2 - 1;
}

function mapRange(value, inMin, inMax, outMin, outMax) {
    return ((value - inMin) * (outMax - outMin)) / (inMax - inMin) + outMin;
}


// Function to calculate head orientation and update sliders
export function calculateHeadOrientation(faceLandmarks) {
    // Check if the face landmarks are valid and non-empty
    if (!Array.isArray(faceLandmarks) || faceLandmarks.length === 0) {
        console.warn("Insufficient face landmarks for head orientation calculation.");
        return { pitch: 0, roll: 0, yaw: 0 };
    }

    // Extract key landmarks for calculations
    const leftEye = faceLandmarks[33];
    const rightEye = faceLandmarks[263];
    const noseTip = faceLandmarks[1]; // e.g., a central point on the nose
    const foreheadCenter = faceLandmarks[151];

    // Check if key landmarks are valid
    if (!leftEye || !rightEye || !noseTip || !foreheadCenter) {
        console.warn("Missing required landmarks for head orientation calculation.");
        return { pitch: 0, roll: 0, yaw: 0 };
    }

    // Validate x, y, z values for each key landmark
    if (
        [leftEye, rightEye, noseTip, foreheadCenter].some(
            landmark => isNaN(landmark.x) || isNaN(landmark.y) || isNaN(landmark.z)
        )
    ) {
        console.warn("Invalid landmark coordinates detected.");
        return { pitch: 0, roll: 0, yaw: 0 };
    }

    // Calculate roll (rotation around the Z-axis)
    const roll = Math.atan2(rightEye.y - leftEye.y, rightEye.x - leftEye.x) * (180 / Math.PI);
    let rollInvert = 1 - roll; // Invert the roll for the slider
    // Calculate pitch (rotation around the X-axis)
    const pitch = Math.atan2(foreheadCenter.y - noseTip.y, foreheadCenter.z - noseTip.z) * (180 / Math.PI);
    let pitchInvert = 1 - pitch; // Invert the pitch for the slider
    // Calculate yaw (rotation around the Y-axis)
    const yaw = Math.atan2(rightEye.x - leftEye.x, noseTip.z - foreheadCenter.z) * (180 / Math.PI);
    let yawInvert = 1 - yaw; // Invert the yaw for the slider

    // console.log('Head Orientation -> Pitch:', pitch, 'Roll:', roll, 'Yaw:', yaw);

    return { pitch: pitch.toFixed(5), roll: roll.toFixed(5), yaw: yaw.toFixed(5) };
}


function updateHeadMotionSliders(pitch, roll, yaw) {
    // Map the values to the sliders, scaling to the range of -1 to 1
    const mappedRoll = mapRange(roll, -90, 90, -1, 1);    // Assuming head tilt ranges from -45 to 45 degrees
    const mappedPitch = mapRange(pitch, -90, 90, -1, 1);  // Assuming nodding ranges from -45 to 45 degrees
    const mappedYaw = mapRange(yaw, -90, 90, -1, 1);      // Assuming head turning ranges from -45 to 45 degrees

    // Output the calculated orientation
    // console.log(`Mapped Head Orientation -> Pitch: ${mappedPitch}, Roll: ${mappedRoll}, Yaw: ${mappedYaw}`);

    const rollSlider = document.getElementById('rollSlider');
    const pitchSlider = document.getElementById('pitchSlider');
    // const yawSlider = document.getElementById('yawSlider');
    rollSlider.value = mappedRoll;
    pitchSlider.value = mappedPitch;
    // yawSlider.value = mappedYaw;
}





// Unified calibration function with conditions check
function performCalibration() {
    // Check all alignment conditions before calibrating
    const rollValue = parseFloat(document.getElementById('rollSlider').value);
    const shouldersAligned = areShouldersAligned(latestPoseResults?.poseLandmarks);

    if (isAligned() && rollValue >= -0.2 && rollValue <= 0.2 && shouldersAligned) {
        if (results && results.faceLandmarks && latestPoseResults && latestPoseResults.poseLandmarks) {
            // Calibrate pose landmarks
            calibrationData.poseLandmarks = latestPoseResults.poseLandmarks.map(landmark => ({ ...landmark }));

            // Calibrate face landmarks (store all detected facial landmarks)
            calibrationData.faceLandmarks = results.faceLandmarks[0].map(landmark => ({ ...landmark }));

            // Calibrate blendshapes
            if (results.faceBlendshapes) {
                calibrationData.faceBlendshapes = results.faceBlendshapes.map(blendshape => ({ ...blendshape }));
            }

            // Calibrate roll
            calibrationData.roll = rollValue;

            console.log('Calibration completed:', calibrationData);
            recordToggle.disabled = false; // Enable recording after calibration
        } else {
            console.log('Calibration failed: Required data not available.');
        }
    } else {
        console.log('Calibration failed: Alignment conditions not met.');
    }
}



// Update the existing calibration button event listener to use the new unified function
// calibrationButton.addEventListener('click', performCalibration);
let isCalibrated = false;
document.getElementById('calibrationButton').addEventListener('click', function () {
    performCalibration();
    isCalibrated = true;    
    // console.log('Calibration button clicked');
});


// Rec helpers
const recordToggle = document.getElementById('recordToggle');

// Disable the recordToggle button initially (enable after calibration)
// recordToggle.disabled = true; //TODO: uncomment when deploying

// Attach an event listener to the record button
recordToggle.addEventListener('click', () => {
    const recording = toggleRecording();
    recordToggle.textContent = recording ? 'Stop Recording' : 'Start Recording';
    recordToggle.style.backgroundColor = recording ? '#fae' : '#f0f0f0';

    if (!recording) {
        console.log('Recording stopped, saving data...');
        // saveRecordedData();  // Save data when recording stops
        saveDataToFile(participantID, sessionID);  
    } else {
        console.log('Recording started...');
    }
});
///////////////////////////////////////////////
///////////////////////////////////////////////

let previousLandmarks = null;
let previousVelocity = null;
let jitterSum = 0;
let frameCount = 0;
const maxFrames = 10;  // A smaller window to detect short-term jittery motion

function detectRealTimePoorLighting(currentLandmarks, jitterThreshold = 0.2, velocityThreshold = 0.05) {
    // Validate input landmarks
    if (!Array.isArray(currentLandmarks) || currentLandmarks.length === 0) {
        console.warn("Invalid current landmarks data for lighting detection, skipping frame.");
        return false;
    }

    // Initialize previousLandmarks and velocity if not already set
    if (!previousLandmarks) {
        previousLandmarks = currentLandmarks.map(landmark => ({ ...landmark }));
        previousVelocity = Array(currentLandmarks.length).fill(0);
        return false;
    }

    let totalVelocity = 0;
    let totalJitter = 0;
    let validLandmarkCount = 0;

    for (let i = 0; i < currentLandmarks.length; i++) {
        const current = currentLandmarks[i];
        const previous = previousLandmarks[i];

        // Check for valid landmark data
        if (!current || !previous || isNaN(current.x) || isNaN(current.y) || isNaN(previous.x) || isNaN(previous.y)) {
            console.warn(`Invalid landmark detected at index ${i}, skipping.`);
            continue;
        }

        // Calculate Euclidean distance between current and previous positions for each landmark
        const dx = current.x - previous.x;
        const dy = current.y - previous.y;
        const dz = (current.z || 0) - (previous.z || 0); // Use z if available, default to 0 if not

        const distance = Math.sqrt(dx * dx + dy * dy + dz * dz);

        // Accumulate velocity
        totalVelocity += distance;
        validLandmarkCount++;

        // Detect rapid changes in velocity as jitter
        if (distance > velocityThreshold && distance < jitterThreshold) {
            totalJitter += distance;
        }
    }

    // If no valid landmarks are found, skip this frame
    if (validLandmarkCount === 0) {
        console.warn("No valid landmarks detected for lighting detection, skipping frame.");
        return false;
    }

    // Calculate average velocity and average jitter for the current frame
    const averageVelocity = totalVelocity / validLandmarkCount;
    const averageJitter = totalJitter / validLandmarkCount;

    // Update running jitter sum and frame count
    jitterSum += averageJitter;
    frameCount++;

    // Calculate the running average jitter over the last `maxFrames` frames
    if (frameCount >= maxFrames) {
        const runningAverageJitter = jitterSum / frameCount;

        // Reset counters to calculate a new running average
        jitterSum = 0;
        frameCount = 0;

        // Update previous landmarks for the next frame
        previousLandmarks = currentLandmarks.map(landmark => ({ ...landmark }));

        // Compare running average jitter to threshold
        return runningAverageJitter > jitterThreshold;
    }

    // Update previous landmarks for the next frame
    previousLandmarks = currentLandmarks.map(landmark => ({ ...landmark }));

    // If not enough frames collected yet, don't flag poor lighting
    return false;
}




///////////////////////////////////////////////
/////////// Initialization ////////////////////
///////////////////////////////////////////////
if (hasGetUserMedia()) {
    getCameraDevices(); // Populate camera dropdown on load
    startTrackingCheckbox.addEventListener("change", toggleTracking); // Add event listener to tracking checkbox
    showWebcamCheckbox.addEventListener("change", toggleWebcamVisibility); // Add event listener to show webcam checkbox
} else {
    console.warn("getUserMedia() is not supported by your browser");
}

// Show or hide the webcam video element based on checkbox state
function toggleWebcamVisibility(event) {
    video.style.display = event.target.checked ? "block" : "none";
}

///////////////////////////////////////////////
///////////////////////////////////////////////
///////////////////////////////////////////////

document.addEventListener('keydown', (event) => {
    const parenthesisShapeLeft = document.getElementById('parenthesisShapeLeft');
    const parenthesisShapeRight = document.getElementById('parenthesisShapeRight');

    if (event.key === 'ArrowLeft') {
        // Show the left parenthesis-like shape
        parenthesisShapeLeft.style.opacity = '1';
        parenthesisShapeLeft.style.border = '5px solid rgba(44, 106, 253)';  // Blue for left
        parenthesisShapeLeft.style.borderRadius = '75px 0 0 75px';  // Left-facing parenthesis
        parenthesisShapeLeft.style.borderRight = 'none';  // Hide right border

        // Hide right parenthesis
        parenthesisShapeRight.style.opacity = '0';  // Hide the right shape
    } else if (event.key === 'ArrowRight') {
        // Show the right parenthesis-like shape
        parenthesisShapeRight.style.opacity = '1';
        parenthesisShapeRight.style.border = '5px solid yellow';  // Yellow for right
        parenthesisShapeRight.style.borderRadius = '0 75px 75px 0';  // Right-facing parenthesis
        parenthesisShapeRight.style.borderLeft = 'none';  // Hide left border

        // Hide left parenthesis
        parenthesisShapeLeft.style.opacity = '0';  // Hide the left shape
    }

    // Hide the visual feedback after a delay (e.g., 500ms)
    clearTimeout(parenthesisShapeLeft.hideTimeout);  // Clear any existing hide timer
    clearTimeout(parenthesisShapeRight.hideTimeout);  // Clear existing hide timer for right
    parenthesisShapeLeft.hideTimeout = setTimeout(() => {
        parenthesisShapeLeft.style.opacity = '0';  // Hide left shape again
    }, 125);  // Delay before hiding again (in milliseconds)

    parenthesisShapeRight.hideTimeout = setTimeout(() => {
        parenthesisShapeRight.style.opacity = '0';  // Hide right shape again
    }, 125);  // Delay before hiding again (in milliseconds)
});


///////////////////////////////////////////////
///////////////////////////////////////////////


// Event listener for startTracking to auto-check showTracking
startTrackingCheckbox.addEventListener("change", (event) => {
    if (event.target.checked) {
        enableDrawingCheckbox.checked = true;
    } else if (!event.target.checked) {
        enableDrawingCheckbox.checked = false;
        showWebcamCheckbox.checked = false;
    }
});